import { apiRequest } from '../client'

export type InventoryItem = {
  restaurantId: number
  restaurantName: string
  availableQty?: number
  safetyBufferQty?: number
  reservedQty: number
  /** Today's announced, unbooked, switched-on incoming quantity. Absent on an older backend. */
  incomingQty?: number
  /** Server-computed sellable (inventory_sellable view). Absent on an older backend. */
  sellableQty?: number
  updatedAt?: string
}

export const inventoryApi = {
  list: (sku: string) =>
    apiRequest<{ items: Array<InventoryItem> }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/inventory`,
    ),
  getOne: (sku: string, restaurantId: number) =>
    apiRequest<InventoryItem>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/inventory/${restaurantId}`,
    ),

  /**
   * `clientRef` is mandatory server-side (CHG-2026-09-pos-sale-ingest-integrity, D6): it is the
   * key that makes a retried Wareneingang book once instead of twice.
   */
  receive: (
    sku: string,
    restaurantId: number,
    quantity: number,
    clientRef: string,
    note?: string,
  ) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/inventory/${restaurantId}/receive`,
      {
        method: 'POST',
        body: JSON.stringify({ quantity, note, clientRef }),
      },
    ),

  setAvailable: (
    sku: string,
    restaurantId: number,
    availableQty: number,
    note?: string,
  ) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/inventory/${restaurantId}/set`,
      {
        method: 'PUT',
        body: JSON.stringify({ availableQty, note }),
      },
    ),

  setSafetyBuffer: (
    sku: string,
    restaurantId: number,
    safetyBufferQty: number,
  ) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/inventory/${restaurantId}/safety-buffer`,
      {
        method: 'PUT',
        body: JSON.stringify({ safetyBufferQty }),
      },
    ),

  createMovement: (payload: {
    type: 'RECEIVE' | 'COUNT'
    restaurantId: number
    lines: Array<{ sku: string; quantity: number }>
    reference?: string
    /** Required for RECEIVE; COUNT is absolute and stays lenient (D6). */
    clientRef?: string
    note?: string
  }) =>
    apiRequest<{ ok: boolean; id: number }>(
      `/api/v0/admin/inventory/movements`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
}
